import { Formik } from "formik";
import { TFunction } from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button, Input, Spacing, Stack } from "@noom/wax-component-library";

import {
  AgeInput,
  AgeInputValidationSchema,
  DateInput,
  DOBSchema,
} from "@/components/form";
import { useAppContext } from "@/contexts";
import { useQueryParams, useTrackEvent } from "@/hooks";
import {
  formatMedicareNumberInput,
  isValidMedicareNumber,
  normalizeMedicareNumber,
} from "@/utils/medicareNumber";
import {
  isCmsAccessEnabled,
  isDobRequired,
  shouldReferToParticipantsAsMembers,
} from "@/utils/partners";

export type EligibilityCheckFormValues = {
  age?: number;
  dob: Date | null;
  employeeId?: string;
  firstName: string;
  lastName: string;
  medicareNumber?: string;
  policyId?: string;
};

export const defaultEligibilityCheckFormValues: EligibilityCheckFormValues = {
  age: undefined,
  dob: null,
  employeeId: "",
  firstName: "",
  lastName: "",
  medicareNumber: "",
  policyId: "",
};

const getValidationSchema = (
  t: TFunction,
  showDobField: boolean,
  showEmployeeIdField: boolean,
  referToParticipantsAsMembers: boolean,
  isUhcPartner: boolean,
  showMedicareNumberField: boolean,
) => {
  const MAX_LENGTH = 150;
  const baseSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(
        MAX_LENGTH,
        t("form.errors.maxLength", {
          label: t("form.firstName.label"),
          maxLength: MAX_LENGTH,
        }),
      )
      .required(t("form.firstName.errors.required")),
    lastName: Yup.string()
      .max(
        MAX_LENGTH,
        t("form.errors.maxLength", {
          label: t("form.lastName.label"),
          maxLength: MAX_LENGTH,
        }),
      )
      .required(t("form.lastName.errors.required")),
  });

  const employeeIdSchema = Yup.object({
    employeeId: Yup.string()
      .max(
        MAX_LENGTH,
        t("form.errors.maxLength", {
          label: t("form.employeeId.label"),
          maxLength: MAX_LENGTH,
        }),
      )
      .required(
        t("form.employeeId.errors.required", {
          context: referToParticipantsAsMembers ? "member" : "",
        }),
      ),
  });

  let validationSchema = baseSchema;

  const ageSchema = showDobField ? DOBSchema : AgeInputValidationSchema;
  validationSchema = validationSchema.concat(ageSchema);

  if (showEmployeeIdField) {
    validationSchema = validationSchema.concat(employeeIdSchema);
  }

  if (isUhcPartner) {
    const policyIdSchema = Yup.object({
      policyId: Yup.string()
        .max(
          MAX_LENGTH,
          t("form.errors.maxLength", {
            label: t("form.policyId.label"),
            maxLength: MAX_LENGTH,
          }),
        )
        .required(t("form.policyId.errors.required")),
    });

    validationSchema = validationSchema.concat(policyIdSchema);
  }

  if (showMedicareNumberField) {
    const medicareNumberSchema = Yup.object({
      medicareNumber: Yup.string()
        .required(t("form.medicareNumber.errors.required"))
        .test(
          "isValidMedicareNumber",
          t("form.medicareNumber.errors.invalid"),
          (value) => !value || isValidMedicareNumber(value),
        ),
    });

    validationSchema = validationSchema.concat(medicareNumberSchema);
  }

  return validationSchema;
};

export const EligibilityCheckForm: React.FC<{
  initialValues: EligibilityCheckFormValues;
  isLoading?: boolean;
  onSubmit: (values: EligibilityCheckFormValues) => void;
  pageId: string;
  setInitialValues: React.Dispatch<EligibilityCheckFormValues>;
}> = ({
  initialValues,
  isLoading,
  pageId,
  setInitialValues,
  onSubmit: onParentSubmit,
}) => {
  const { enrollmentType, partnerInfo } = useAppContext();
  const queryParams = useQueryParams();
  const { trackAnonEvent, trackIdentifiedPageViewed } = useTrackEvent();
  const [isMedicareNumberFocused, setIsMedicareNumberFocused] = useState(false);
  const { i18n, t } = useTranslation();

  const { configs, slug } = partnerInfo || {};
  const { eligibilityId } = queryParams;

  const isUhcPartner =
    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "UHC_OPTUM_BIG_FIVE";
  const referToParticipantsAsMembers =
    shouldReferToParticipantsAsMembers(partnerInfo);

  const isCmsAccess = isCmsAccessEnabled(partnerInfo);

  // ACCESS has no eligibility file, so it never "checks eligibility" here.
  const ctaText =
    isCmsAccess ||
    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "NONE" ||
    enrollmentType === "RETRIAGE"
      ? t("common.next")
      : t("eligibilityCheck.ctaText.checkEligibility");
  const showDobField = isDobRequired(partnerInfo);
  const showEmployeeIdField =
    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "PARTICIPANT_ID" ||
    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "PARTICIPANT_ID_LAST_NAME" ||
    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "PARTICIPANT_ID_FULL_NAME" ||
    isUhcPartner;
  const showMedicareNumberField = isCmsAccess;

  const handleClickCta = () => {
    // Only ACCESS tracks this tap, other partners keep their current events.
    if (isCmsAccess) {
      trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
        buttonName: "Next",
        pageId,
      });
    }
  };

  const onSubmit = async (values: EligibilityCheckFormValues) => {
    // Only keep the Medicare number for partners that collect it, so other
    // partners' survey answers don't gain an empty key.
    const { medicareNumber, ...otherValues } = values;
    const trimmedValues: EligibilityCheckFormValues = {
      ...otherValues,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      ...(showMedicareNumberField &&
        medicareNumber && {
          medicareNumber: normalizeMedicareNumber(medicareNumber),
        }),
    };

    // Store the values in local React state so that they persist when clicking
    // back on the eligibility error state
    setInitialValues(trimmedValues);
    onParentSubmit(trimmedValues);
  };

  useEffect(() => {
    trackIdentifiedPageViewed(pageId);
  }, []);

  // The stored Medicare number has no dashes, so show it masked again when the
  // form comes back after an eligibility error.
  const formInitialValues = {
    ...initialValues,
    medicareNumber: formatMedicareNumberInput(
      initialValues.medicareNumber ?? "",
    ),
  };

  return (
    <Formik
      initialValues={formInitialValues}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(
        t,
        showDobField,
        showEmployeeIdField,
        referToParticipantsAsMembers,
        isUhcPartner,
        showMedicareNumberField,
      )}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({
        errors,
        getFieldProps,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        touched,
        validateField,
      }) => {
        const handleInputChange = async (
          e: React.ChangeEvent<HTMLInputElement>,
          fieldName: keyof EligibilityCheckFormValues,
        ) => {
          // This is a hacky method to get each field to validate on change
          // individually.
          await handleChange(e);
          validateField(fieldName);
        };

        // The Medicare number is masked as the member types or pastes, and is
        // validated only on Continue. Once an error is shown, it is re-checked
        // on every edit so it clears as soon as the value becomes valid.
        const handleMedicareNumberChange = async (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          await setFieldValue(
            "medicareNumber",
            formatMedicareNumberInput(e.target.value),
            false,
          );
          if (errors.medicareNumber) {
            validateField("medicareNumber");
          }
        };
        const medicareNumberFieldProps = getFieldProps("medicareNumber");

        return (
          <form onSubmit={handleSubmit}>
            <Stack spacing={Spacing[8]}>
              <Input
                {...getFieldProps("firstName")}
                autoFocus
                error={touched.firstName && errors.firstName}
                label={t("form.firstName.label")}
                onChange={(e) => handleInputChange(e, "firstName")}
                placeholder={t("form.firstName.placeholder")}
                isReadOnly={Boolean(
                  initialValues.firstName &&
                  eligibilityId &&
                  configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "FULL_NAME_DOB",
                )}
              />
              <Input
                {...getFieldProps("lastName")}
                error={touched.lastName && errors.lastName}
                label={t("form.lastName.label")}
                onChange={(e) => handleInputChange(e, "lastName")}
                placeholder={t("form.lastName.placeholder")}
                isReadOnly={Boolean(
                  initialValues.lastName &&
                  eligibilityId &&
                  (configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT ===
                    "FULL_NAME_DOB" ||
                    configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "LN_DOB"),
                )}
              />
              {showDobField ? (
                <DateInput
                  {...getFieldProps("dob")}
                  error={touched.dob && errors.dob}
                  label={t("form.dob.label")}
                  onChange={() => validateField("dob")}
                  placeholder={t("form.dob.placeholder")}
                  isReadOnly={Boolean(
                    initialValues.dob &&
                    eligibilityId &&
                    (configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT ===
                      "FULL_NAME_DOB" ||
                      configs?.UPFRONT_ELIGIBILITY_ENFORCEMENT === "LN_DOB"),
                  )}
                />
              ) : (
                <AgeInput
                  name="age"
                  onChange={(e) => handleInputChange(e, "age")}
                />
              )}
              {showEmployeeIdField && (
                <Input
                  {...getFieldProps("employeeId")}
                  error={touched.employeeId && errors.employeeId}
                  helper={
                    i18n.exists(`form.employeeId.helper_${slug}`)
                      ? t(`form.employeeId.helper_${slug}`)
                      : undefined
                  }
                  label={t("form.employeeId.label", {
                    context: referToParticipantsAsMembers ? "member" : "",
                  })}
                  onChange={(e) => handleInputChange(e, "employeeId")}
                  placeholder={t("form.employeeId.placeholder", {
                    context: referToParticipantsAsMembers ? "member" : "",
                  })}
                  isReadOnly={Boolean(
                    // This field is only shown on this form if it's part of
                    // upfront eligibility enforcement.
                    initialValues.employeeId && eligibilityId,
                  )}
                />
              )}
              {isUhcPartner && (
                <Input
                  {...getFieldProps("policyId")}
                  error={touched.policyId && errors.policyId}
                  label={t("form.policyId.label")}
                  onChange={(e) => handleInputChange(e, "policyId")}
                  placeholder={t("form.policyId.placeholder")}
                />
              )}
              {showMedicareNumberField && (
                <Input
                  {...medicareNumberFieldProps}
                  // Not in wax's InputProps types, but forwarded to the input:
                  // uppercase letters and no autocorrect on mobile keyboards.
                  {...{
                    autoCapitalize: "characters",
                    autoComplete: "off",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                  error={touched.medicareNumber && errors.medicareNumber}
                  helper={t("form.medicareNumber.helper")}
                  label={t("form.medicareNumber.label")}
                  onBlur={(e) => {
                    setIsMedicareNumberFocused(false);
                    medicareNumberFieldProps.onBlur(e);
                  }}
                  onChange={handleMedicareNumberChange}
                  onFocus={() => setIsMedicareNumberFocused(true)}
                  placeholder={
                    isMedicareNumberFocused
                      ? undefined
                      : t("form.medicareNumber.placeholder")
                  }
                />
              )}
              <Button
                colorScheme="primary"
                isLoading={isLoading || isSubmitting}
                onClick={handleClickCta}
                size="xl"
                type="submit"
              >
                {ctaText}
              </Button>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
};
